import { Head, router } from '@inertiajs/react';
import { FormEvent, useState } from 'react';

import { JourneyLayout } from '@/layouts/journey/journey-layout';
import type { CreateSessionResponse, GMData } from '@/lib/journey-types';

export default function JourneyIndex() {
    const [companyName, setCompanyName] = useState('');
    const [backgroundUrl, setBackgroundUrl] = useState('');
    const [importData, setImportData] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showImport, setShowImport] = useState(false);

    const handleCreate = async (e: FormEvent) => {
        e.preventDefault();
        if (!companyName.trim()) {
            setError('Please enter a company name');
            return;
        }

        setIsCreating(true);
        setError(null);

        try {
            const response = await fetch('/journey/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN':
                        document.querySelector<HTMLMetaElement>(
                            'meta[name="csrf-token"]',
                        )?.content || '',
                },
                body: JSON.stringify({
                    company_name: companyName.trim(),
                    background_url: backgroundUrl.trim() || null,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to create journey');
            }

            const result: CreateSessionResponse = await response.json();

            // Store GM token in localStorage
            const gmData: GMData = {
                sessionId: result.session_id,
                gmToken: result.gm_token,
            };
            localStorage.setItem(
                `journey:gm:${result.session_id}`,
                JSON.stringify(gmData),
            );

            // Navigate to the session
            router.visit(`/journey/${result.session_id}`);
        } catch {
            setError('Failed to create journey. Please try again.');
            setIsCreating(false);
        }
    };

    const handleImport = async (e: FormEvent) => {
        e.preventDefault();

        if (!importData.trim()) {
            setError('Please paste journey data to import');
            return;
        }

        setIsImporting(true);
        setError(null);

        try {
            const parsedData = JSON.parse(importData);

            const response = await fetch('/journey/import', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN':
                        document.querySelector<HTMLMetaElement>(
                            'meta[name="csrf-token"]',
                        )?.content || '',
                },
                body: JSON.stringify({ data: parsedData }),
            });

            if (!response.ok) {
                throw new Error('Failed to import journey');
            }

            const result: CreateSessionResponse = await response.json();

            // Store GM token in localStorage
            const gmData: GMData = {
                sessionId: result.session_id,
                gmToken: result.gm_token,
            };
            localStorage.setItem(
                `journey:gm:${result.session_id}`,
                JSON.stringify(gmData),
            );

            // Navigate to the session
            router.visit(`/journey/${result.session_id}`);
        } catch {
            setError('Invalid journey data. Please check the JSON format.');
            setIsImporting(false);
        }
    };

    return (
        <JourneyLayout>
            <Head title="Start a Journey" />

            <div className="flex min-h-screen items-center justify-center p-4">
                <div className="journey-card w-full max-w-lg">
                    <div className="mb-6 text-center">
                        <h1 className="journey-section-title mb-2 border-b-0 pb-0 text-2xl">
                            The One Ring
                        </h1>
                        <p
                            className="text-[var(--journey-brown)]"
                            style={{ fontFamily: 'var(--font-crimson)' }}
                        >
                            Journey Tracker
                        </p>
                    </div>

                    {error && (
                        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {!showImport ? (
                        <>
                            <form onSubmit={handleCreate} className="space-y-4">
                                <div>
                                    <label
                                        htmlFor="companyName"
                                        className="mb-1.5 block text-sm font-medium"
                                        style={{
                                            fontFamily: 'var(--font-cinzel)',
                                            color: 'var(--journey-brown-dark)',
                                        }}
                                    >
                                        Company Name
                                    </label>
                                    <input
                                        id="companyName"
                                        type="text"
                                        value={companyName}
                                        onChange={(e) =>
                                            setCompanyName(e.target.value)
                                        }
                                        placeholder="e.g., The Fellowship"
                                        className="journey-input w-full px-3 py-2"
                                        autoFocus
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="backgroundUrl"
                                        className="mb-1.5 block text-sm font-medium"
                                        style={{
                                            fontFamily: 'var(--font-cinzel)',
                                            color: 'var(--journey-brown-dark)',
                                        }}
                                    >
                                        Map Background URL (Optional)
                                    </label>
                                    <input
                                        id="backgroundUrl"
                                        type="url"
                                        value={backgroundUrl}
                                        onChange={(e) =>
                                            setBackgroundUrl(e.target.value)
                                        }
                                        placeholder="https://example.com/middle-earth-map.jpg"
                                        className="journey-input w-full px-3 py-2"
                                    />
                                    <p
                                        className="mt-1 text-xs"
                                        style={{
                                            color: 'var(--journey-brown)',
                                        }}
                                    >
                                        Provide a URL to a map image for the hex
                                        grid background
                                    </p>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isCreating}
                                    className="journey-button journey-button-primary w-full py-2.5"
                                >
                                    {isCreating
                                        ? 'Creating Journey...'
                                        : 'Begin Journey'}
                                </button>
                            </form>

                            <div className="mt-4 text-center">
                                <button
                                    type="button"
                                    onClick={() => setShowImport(true)}
                                    className="text-sm hover:underline"
                                    style={{ color: 'var(--journey-brown)' }}
                                >
                                    Or import an existing journey
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <form onSubmit={handleImport} className="space-y-4">
                                <div>
                                    <label
                                        htmlFor="importData"
                                        className="mb-1.5 block text-sm font-medium"
                                        style={{
                                            fontFamily: 'var(--font-cinzel)',
                                            color: 'var(--journey-brown-dark)',
                                        }}
                                    >
                                        Journey Data (JSON)
                                    </label>
                                    <textarea
                                        id="importData"
                                        value={importData}
                                        onChange={(e) =>
                                            setImportData(e.target.value)
                                        }
                                        placeholder='{"company_name": "...", ...}'
                                        className="journey-textarea w-full px-3 py-2"
                                        rows={8}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isImporting}
                                    className="journey-button journey-button-primary w-full py-2.5"
                                >
                                    {isImporting
                                        ? 'Importing...'
                                        : 'Import Journey'}
                                </button>
                            </form>

                            <div className="mt-4 text-center">
                                <button
                                    type="button"
                                    onClick={() => setShowImport(false)}
                                    className="text-sm hover:underline"
                                    style={{ color: 'var(--journey-brown)' }}
                                >
                                    Back to create new journey
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </JourneyLayout>
    );
}
